/**
 * React Query Client Configuration
 *
 * Centralized QueryClient with polling support for real-time updates.
 */

import { QueryClient } from '@tanstack/react-query';

// Poll interval constants
export const POLL_INTERVALS = {
  DASHBOARD: 30 * 1000, // 30 seconds
  EVENT_DETAIL: 30 * 1000, // 30 seconds (was 15s — payload trimmed, less need to refresh aggressively)
  EVENT_LIST: 60 * 1000, // 60 seconds
  DISABLED: false as const,
} as const;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is considered fresh for 30 seconds
      staleTime: 30 * 1000,

      // Keep unused data in cache for 5 minutes
      gcTime: 5 * 60 * 1000,

      // Retry failed requests up to 3 times with exponential backoff
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Don't refetch on window focus by default
      refetchOnWindowFocus: false,

      // Refetch on reconnect
      refetchOnReconnect: true,
    },
  },
});

// Query key factory for consistent key management
export const queryKeys = {
  // Dashboard
  adminDashboard: ['dashboard', 'admin'] as const,
  crewDashboard: ['dashboard', 'crew'] as const,

  // Events
  events: {
    all: ['events'] as const,
    lists: () => [...queryKeys.events.all, 'list'] as const,
    list: (filters: Record<string, unknown>) =>
      [...queryKeys.events.lists(), filters] as const,
    details: () => [...queryKeys.events.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.events.details(), id] as const,
  },

  // Pay periods (per org; the report filter bar snaps dates to them)
  payPeriods: {
    all: ['payPeriods'] as const,
    list: (orgSlug: string) => [...queryKeys.payPeriods.all, 'list', orgSlug] as const,
  },

  // Resources (per org; the hook appends the slug)
  resources: ['resources'] as const,

  // Assignments
  assignments: {
    all: ['assignments'] as const,
    my: () => [...queryKeys.assignments.all, 'my'] as const,
    byEvent: (eventId: string) =>
      [...queryKeys.assignments.all, 'event', eventId] as const,
    availableCrew: (eventId: string, roleId: string) =>
      [...queryKeys.assignments.all, 'available-crew', eventId, roleId] as const,
  },

  // Availability
  availability: {
    all: ['availability'] as const,
    my: () => [...queryKeys.availability.all, 'my'] as const,
    pending: () => [...queryKeys.availability.all, 'pending'] as const,
    byEvent: (eventId: string) =>
      [...queryKeys.availability.all, 'event', eventId] as const,
  },

  // Users
  users: {
    all: ['users'] as const,
    detail: (id: string) => [...queryKeys.users.all, 'detail', id] as const,
  },

  // Documents
  documents: {
    all: ['documents'] as const,
    byEvent: (eventId: string) => [...queryKeys.documents.all, 'event', eventId] as const,
  },
} as const;
